import type { ApprovalBarProps } from "@/components/assistant/useAssistantWriteApprovalHandlers";
import { cn } from "@/lib/utils";
import type { DraftClauseAlternative } from "@/types/drafting";
import {
  formatClauseCategoryLabel,
  formatClauseVariantLabel,
} from "@/lib/clause-variant-labels";
import type { DraftClauseLibraryProposalInput } from "./types";

type Props = {
  mode: "create" | "update";
  proposal: DraftClauseLibraryProposalInput;
  existing?: DraftClauseAlternative | null;
  sectionTitle?: string;
  disabled?: boolean;
  showActions?: boolean;
  approvalBar?: ApprovalBarProps;
};

function FieldRow({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  return (
    <div className="grid gap-1.5 sm:grid-cols-[8.5rem_1fr] sm:items-start">
      <span className="text-xs font-medium uppercase tracking-wide text-[var(--lm-muted)] dark:text-slate-400">
        {label}
      </span>
      <p
        className={cn(
          "break-words whitespace-pre-wrap text-[15px] leading-snug text-[var(--lm-foreground-strong)] dark:text-slate-100",
          multiline && "max-h-40 overflow-y-auto text-sm"
        )}
      >
        {value || "—"}
      </p>
    </div>
  );
}

export function DraftClauseLibraryProposalCard({
  mode,
  proposal,
  existing,
  sectionTitle,
  showActions = true,
}: Props) {
  const title = mode === "create" ? "Create clause variant" : "Update clause variant";
  const sectionKey = proposal.section_key ?? existing?.section_key ?? "";
  const category = proposal.clause_category ?? existing?.clause_category ?? null;
  const posture = proposal.posture ?? existing?.posture ?? null;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-[rgba(15,23,42,0.09)] bg-white/80 dark:border-white/10 dark:bg-white/[0.04]"
      )}
    >
      <div className="border-b border-[rgba(15,23,42,0.09)] px-3 py-2 dark:border-white/10">
        <p className="text-sm font-semibold text-[var(--lm-foreground-strong)] dark:text-slate-100">
          {title}
        </p>
        {!showActions ? (
          <p className="text-xs text-[var(--lm-muted)] dark:text-slate-400">
            Use View Plan or Create below the chat when ready.
          </p>
        ) : null}
      </div>
      <div className="space-y-3 px-3 py-3">
        <FieldRow label="Section" value={sectionTitle ? `${sectionTitle} (${sectionKey})` : sectionKey} />
        <FieldRow label="Category" value={formatClauseCategoryLabel(category)} />
        <FieldRow label="Posture" value={formatClauseVariantLabel(posture, category)} />
        <FieldRow
          label="Jurisdiction"
          value={proposal.jurisdiction_applicability ?? existing?.jurisdiction_applicability ?? "general"}
        />
        <FieldRow label="Version" value={proposal.version ?? existing?.version ?? "v1.0"} />
        <FieldRow label="Language" value={proposal.language ?? existing?.language ?? ""} multiline />
      </div>
    </div>
  );
}
